import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tasksApi } from '../../api/tasks';
import ClientTaskDetail from './ClientTaskDetail';
import PageHeader from '../../components/PageHeader';

interface ClientTaskRow {
  id: string;
  title: string;
  status: string;
  priority?: string | null;
  due_date?: string | null;
  created_at?: string | null;
  last_activity_at?: string | null;
  partner_name?: string | null;
}

const PRIORITY_BADGE: Record<string, string> = {
  URGENT: 'bg-red-100 text-red-700 ring-red-200',
  HIGH: 'bg-orange-100 text-orange-700 ring-orange-200',
  MEDIUM: 'bg-amber-100 text-amber-700 ring-amber-200',
  LOW: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
};

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700 ring-gray-200',
  TODO: 'bg-blue-100 text-blue-700 ring-blue-200',
  IN_PROGRESS: 'bg-indigo-100 text-indigo-700 ring-indigo-200',
  IN_REVIEW: 'bg-purple-100 text-purple-700 ring-purple-200',
  REVIEW: 'bg-purple-100 text-purple-700 ring-purple-200',
  DONE: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  COMPLETED: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  CANCELLED: 'bg-red-100 text-red-700 ring-red-200',
  REJECTED: 'bg-red-100 text-red-700 ring-red-200',
  ON_HOLD: 'bg-yellow-100 text-yellow-800 ring-yellow-200',
};

function badge(map: Record<string, string>, key?: string | null) {
  if (!key) return 'bg-gray-100 text-gray-600 ring-gray-200';
  return map[key.toUpperCase()] || 'bg-gray-100 text-gray-600 ring-gray-200';
}

function fmtDate(s?: string | null) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return s;
  }
}

function fmtRelative(s?: string | null) {
  if (!s) return '';
  try {
    const d = new Date(s);
    const diff = Date.now() - d.getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

export default function ClientPortal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<ClientTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [openTask, setOpenTask] = useState<string | null>(searchParams.get('task'));

  // Sync the open-task state when the URL changes (e.g. user clicks a
  // CLIENT_TASK_* notification while already on the portal).
  useEffect(() => {
    const t = searchParams.get('task');
    if (t && t !== openTask) {
      setOpenTask(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const closeTask = () => {
    setOpenTask(null);
    if (searchParams.has('task')) {
      const next = new URLSearchParams(searchParams);
      next.delete('task');
      setSearchParams(next, { replace: true });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await tasksApi.listTasksForMeAsClient();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.title || '').toLowerCase().includes(q) ||
        (r.partner_name || '').toLowerCase().includes(q) ||
        (r.status || '').toLowerCase().includes(q),
    );
  }, [rows, query]);

  return (
    <div className="p-4 md:p-6">
      <PageHeader
        eyebrow="I. Partner Portal"
        title={<>My <em>Tasks</em></>}
        subtitle={<>Tasks created <em>for you</em>. Open any task to view details, comments and post a reply.</>}
        actions={
          <>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title, partner, status"
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 w-64"
            />
            <button
              type="button"
              onClick={load}
              className="gt-btn"
            >
              Refresh
            </button>
          </>
        }
      />
      <div className="h-4" />

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-gray-500">
          Loading your tasks...
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-gray-700 font-medium">
            {query ? 'No tasks match your search.' : 'No tasks for you yet.'}
          </p>
          {!query && (
            <p className="text-sm text-gray-500 mt-1">
              When a partner creates a task for you, it will appear here.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-auto max-h-[70vh] rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="sticky top-0 z-10 bg-gray-50 text-left px-4 py-3 font-medium">Title</th>
                <th className="sticky top-0 z-10 bg-gray-50 text-left px-4 py-3 font-medium">From</th>
                <th className="sticky top-0 z-10 bg-gray-50 text-left px-4 py-3 font-medium">Status</th>
                <th className="sticky top-0 z-10 bg-gray-50 text-left px-4 py-3 font-medium">Priority</th>
                <th className="sticky top-0 z-10 bg-gray-50 text-left px-4 py-3 font-medium">Due</th>
                <th className="sticky top-0 z-10 bg-gray-50 text-left px-4 py-3 font-medium">Last update</th>
                <th className="sticky top-0 z-10 bg-gray-50 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setOpenTask(r.id)}
                  className="hover:bg-emerald-50/40 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{r.title}</td>
                  <td className="px-4 py-3">
                    <span className="text-gray-900">
                      {r.partner_name || <span className="text-gray-400 italic">Partner</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset ${badge(STATUS_BADGE, r.status)}`}
                    >
                      {(r.status || '—').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset ${badge(PRIORITY_BADGE, r.priority)}`}
                    >
                      {(r.priority || '—').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{fmtDate(r.due_date)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {fmtRelative(r.last_activity_at || r.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-emerald-700 text-xs font-semibold hover:underline">
                      Open &rsaquo;
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openTask && (
        <ClientTaskDetail
          taskId={openTask}
          onClose={closeTask}
          onChanged={load}
        />
      )}
    </div>
  );
}
